/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: metrics-filter-modal-component.ts
 *
 * Created by randysorensen on 2019-02-14.
 */

function MetricsFilterModalController (
    this: JS5Object,
    _: JS5Object,
    DynamicPropertiesService: JS5Object,
    MetricsDashboardService: JS5Object,
    UtilsService: JS5Object
) {
    let allPartners: JS5Object[] = [];

    const createYearsCollection = () => {
        const currentYear = new Date().getFullYear();
        const startYear = UtilsService.constants.EARLIEST_RECORD_YEAR;

        this.years = _.range(startYear, currentYear + 1);
    };


    //=======================//
    // Filter Getter/Setters //
    //=======================//

    this.fromDateMonth = (newMonth: string=null) => {
        if (newMonth) {
            this.currentCriteria.fromDate.setMonth(_.indexOf(UtilsService.constants.MONTHS, newMonth));
        }
        return UtilsService.constants.MONTHS[this.currentCriteria.fromDate.getMonth()];
    };

    this.fromDateYear = (newYear: string=null) => {
        if (newYear) {
            this.currentCriteria.fromDate.setFullYear(newYear);
        }
        return this.currentCriteria.fromDate.getFullYear();
    };

    this.toDateMonth = (newMonth: string=null) => {
        if (newMonth) {
            this.currentCriteria.toDate.setMonth(_.indexOf(UtilsService.constants.MONTHS, newMonth));
        }
        return UtilsService.constants.MONTHS[this.currentCriteria.toDate.getMonth()];
    };

    this.toDateYear = (newYear: string=null) => {
        if (newYear) {
            this.currentCriteria.toDate.setFullYear(newYear);
        }
        return this.currentCriteria.toDate.getFullYear();
    };


    //===================//
    // Template Controls //
    //===================//

    const deselectPartnersBasedOnType = () => {
        let selectedPartnerTypes = this.currentCriteria.partnerTypes;
        let selectedPartners = _.cloneDeep(this.currentCriteria.partners);
        _.remove(selectedPartners, (partner: JS5Object) => {
            let otherEnabled = _.includes(selectedPartnerTypes, 'Other');
            let partnerTypeIsOther = !_.includes(this.availablePartnerTypes, partner.type);

            let removePartner = false;
            if (partnerTypeIsOther) {
                removePartner = !otherEnabled;
            } else {
                removePartner = !_.includes(selectedPartnerTypes, partner.type);
            }

            return removePartner;
        });
        this.currentCriteria.partners = selectedPartners;
    };

    this.onPartnerTypeSelectionsChanged = () => {
        if (this.currentCriteria.partnerTypes.length > 0) {
            deselectPartnersBasedOnType();
        }
        updatePartnerDropdownLabel();
    };

    this.onAcceptCriteria = ($event: JS5Object, setToDefault: boolean) => {
        $event.target.blur();
        $event.stopImmediatePropagation();

        if (setToDefault) {
            this.currentCriteria = MetricsDashboardService.createDefaultCriteria();
        }

        if (this.currentCriteria.partners.length === allPartners.length) {
            this.currentCriteria.partners = [];
        }

        MetricsDashboardService.setCurrentCriteria(this.currentCriteria);
        this.close();
    };

    this.dateRangeIsInvalid = (): boolean => {
        let nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return (
            this.currentCriteria.fromDate > this.currentCriteria.toDate ||
            this.currentCriteria.fromDate >= nextMonth ||
            this.currentCriteria.toDate >= nextMonth
        );
    };

    this.applyButtonEnabled = (): boolean => !this.dateRangeIsInvalid();

    //
    // Returns a list of partners filtered by the specified
    // partner types
    //
    const filterPartnersByPartnerTypes = (selectedPartnerTypes: string[]) => {
        return _.filter(allPartners, (partner: JS5Object) => {
            let typeMatch = _.includes(selectedPartnerTypes, partner.type);

            // partner has no type and 'Other' is selected
            let noTypeMatch = (
                _.includes(selectedPartnerTypes, 'Other') &&
                !_.includes(this.availablePartnerTypes, partner.type)
            );
            return typeMatch || noTypeMatch;
        });
    };

    //
    // A memoize'd variant of the above function which prevents
    // AngularJS from over-$digest()'ing
    //
    const memoizedFilterPartnerByPartnerTypes = _.memoize(
        filterPartnersByPartnerTypes,
        (key: string[]) => key.join('|')
    );

    this.getAvailablePartners = () => {
        let partners = allPartners;
        if (this.currentCriteria.partnerTypes.length > 0) {
            partners = memoizedFilterPartnerByPartnerTypes(this.currentCriteria.partnerTypes);
        }
        return partners;
    };

    const updatePartnerDropdownLabel = () => {
        let label = 'Select ';
        if (_.get(this, 'currentCriteria.partnerTypes', []).length) {
            label += this.currentCriteria.partnerTypes.join(' / ') + ' ';
        }
        label += 'Partner(s)';
        this.partnerDropdownLabel = label;
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        allPartners = MetricsDashboardService.getAllAvailablePartners();
        DynamicPropertiesService.queryForProperties().then(() => {
            this.availablePartnerTypes = _
                .concat(DynamicPropertiesService.getPropValueAsList('partner.types'));
        });
        this.months = _.cloneDeep(UtilsService.constants.MONTHS);
        createYearsCollection();
        updatePartnerDropdownLabel();
        this.currentCriteria = MetricsDashboardService.getCurrentCriteria();
    };

}

angular.module('prism-ui.components').component('metricsFilterModal', {
    controller: MetricsFilterModalController,
    templateUrl: 'dashboard/metrics/metrics-filter-modal/metrics-filter-modal-template.html',
    bindings: {
        close: '&',
        dismiss: '&',
        resolve: '<'
    }
});
